/**
 * The run behind SES-011's answer (fixture-decisions.ts): the open decisions
 * due by Friday in the Operations channel, the emails behind them in
 * Outlook, the supplier's bill in Xero and the renewal quote in Google
 * Drive, then the answer. The trace lines and progress line are what the
 * application derives from these events; fixture-decisions.test.ts
 * re-derives them at every point of the run.
 */

import { runActivityLabel } from "@product/components/app/runs/run-trace-labels";
import type { PluginIconPlugin } from "@product/components/content/plugin-icon";
import { formatRunDuration } from "@product/lib/run-details-format";
import { buildRunSourceUsage } from "@product/lib/run-source-usage";

import { CALDER_NOW } from "./fixture-calder";
import {
  DECISIONS,
  DECISIONS_ANSWER_MARKDOWN,
  DECISIONS_ANSWER_RECORD_IDS,
  DECISIONS_CHANNEL,
  DECISIONS_DATA_SET_NAME,
  DECISIONS_DUE,
  DECISIONS_QUESTION,
  DECISIONS_SESSIONS_RUNNING,
  DECISIONS_TURN_TIMES,
  DECISIONS_WORKSPACE,
} from "./fixture-decisions";
import { jsonResult, replayEvents, type ReplayRun } from "./replay-run";
import type { ReplayStory } from "./replay-story";
import { decisionSheetRecord } from "./sheet-record";
import type { ShotStory } from "./story";

/** Each tool as the executor names it: the plugin's label and its tool's label. */
const WORKSPACE_TOOL = {
  toolName: "twin_workspace_query_channel_records",
  toolLabel: "Query channel records",
  pluginSlug: "mlx-workspace",
  pluginLabel: "Twin workspace",
} as const;
const OUTLOOK_TOOL = {
  toolName: "microsoft_outlook_search_messages",
  toolLabel: "Search Outlook messages",
  pluginSlug: "microsoft-outlook",
  pluginLabel: "Microsoft Outlook Mail",
} as const;
const XERO_TOOL = {
  toolName: "xero_accounting_read",
  toolLabel: "Xero Accounting API read",
  pluginSlug: "xero",
  pluginLabel: "Xero",
} as const;
const DRIVE_TOOL = {
  toolName: "warehouse_query",
  toolLabel: "Query ingested Google Drive data",
  pluginSlug: "google-drive",
  pluginLabel: "Google Drive",
} as const;

/** Asked at 09:11; the run takes 22.4 seconds. */
export const DECISIONS_RUN: ReplayRun = {
  id: "run_decisions_friday",
  eventPrefix: "evt_decisions",
  startedAt: CALDER_NOW - 60_000,
  durationMs: 22_400,
  steps: [
    ["run.queued", 0, { source: "web" }],
    ["run.claimed", 380, { workerId: "executor-2" }],
    ["tool.started", 1_200, { toolCallId: "call_decisions", ...WORKSPACE_TOOL, args: { channel: DECISIONS_CHANNEL.name, dataSet: DECISIONS_DATA_SET_NAME, where: { status: "open", dueOnOrBefore: DECISIONS_DUE } } }],
    ["tool.completed", 2_000, { toolCallId: "call_decisions", ...WORKSPACE_TOOL, result: jsonResult({ records: DECISIONS.map((decision) => ({ id: decision.id, title: decision.name })) }) }],
    ["tool.started", 2_400, { toolCallId: "call_email", ...OUTLOOK_TOOL, args: { query: '"Northgate" OR "INV-8812" OR "cleaning contract"', top: 25 } }],
    ["tool.completed", 3_800, { toolCallId: "call_email", ...OUTLOOK_TOOL, result: jsonResult({ messages: 3 }) }],
    ["tool.started", 4_200, { toolCallId: "call_bill", ...XERO_TOOL, args: { method: "GET", path: "/Invoices", where: 'Type=="ACCPAY" AND InvoiceNumber=="INV-8812"' } }],
    ["tool.completed", 5_600, { toolCallId: "call_bill", ...XERO_TOOL, result: jsonResult({ Invoices: [{ InvoiceNumber: "INV-8812", AmountDue: 6_480, DueDate: "2026-09-30" }] }) }],
    ["tool.started", 6_000, { toolCallId: "call_quote", ...DRIVE_TOOL, args: {} }],
    ["tool.updated", 6_300, { toolCallId: "call_quote", ...DRIVE_TOOL, args: {}, partialResult: { details: { reason: "Finding Ashgrove's renewal quote" } } }],
    ["tool.completed", 6_900, { toolCallId: "call_quote", ...DRIVE_TOOL, result: { ...jsonResult({ rows: 1 }), details: { rowCount: 1, durationMs: 71 } } }],
  ],
  stream: { startsMs: 7_400, endsMs: 21_800 },
  statusLines: {
    evt_decisions_001: { title: "Queued", description: "The prompt was stored in Postgres and is waiting for a worker.", tone: "warning" },
    evt_decisions_002: { title: "Claimed", description: "Claimed by executor-2.", tone: "info" },
    evt_decisions_003: { title: "Query channel records", description: "Query channel records started.", tone: "info" },
    evt_decisions_004: { title: "Query channel records", description: "Query channel records finished successfully.", tone: "success" },
    evt_decisions_005: { title: "Search Outlook messages", description: "Search Outlook messages started.", tone: "info" },
    evt_decisions_006: { title: "Search Outlook messages", description: "Search Outlook messages finished successfully.", tone: "success" },
    evt_decisions_007: { title: "Xero Accounting API read", description: "Xero Accounting API read started.", tone: "info" },
    evt_decisions_008: { title: "Xero Accounting API read", description: "Xero Accounting API read finished successfully.", tone: "success" },
    evt_decisions_010: { title: "Running a query", description: "Finding Ashgrove's renewal quote", tone: "info" },
    evt_decisions_011: { title: "Query finished", description: "1 row in 71ms", tone: "success" },
    evt_decisions_149: { title: "Completed", description: "Final output was written back to Postgres.", tone: "success" },
  },
  liveUpdates: [
    { id: "evt_decisions_001", atMs: 0, text: "Waiting to start" },
    { id: "evt_decisions_002", atMs: 380, text: "Starting analysis" },
    { id: "evt_decisions_003", atMs: 1_200, text: "Checking channel records" },
    { id: "evt_decisions_004", atMs: 2_000, text: "Channel records checked" },
    { id: "evt_decisions_005", atMs: 2_400, text: "Checking Microsoft Outlook Mail" },
    { id: "evt_decisions_006", atMs: 3_800, text: "Checked Microsoft Outlook Mail" },
    { id: "evt_decisions_007", atMs: 4_200, text: "Checking Xero" },
    { id: "evt_decisions_008", atMs: 5_600, text: "Checked Xero" },
    { id: "evt_decisions_010", atMs: 6_300, text: "Searching Google Drive" },
    { id: "evt_decisions_011", atMs: 6_900, text: "Checked Google Drive" },
  ],
};

/** The plugins the run used, with the icons the app's catalogue names. */
const PLUGINS: PluginIconPlugin[] = [
  { slug: "mlx-workspace", label: "Twin workspace", branding: { iconUrl: "/concepts/product-shot/icons/mlx/workspace.svg" } },
  {
    slug: "microsoft-outlook",
    label: "Microsoft Outlook Mail",
    branding: { iconUrl: "/concepts/product-shot/icons/sources/outlook-mail.svg" },
  },
  { slug: "xero", label: "Xero", branding: { iconUrl: "/concepts/product-shot/icons/sources/xero.svg" } },
  { slug: "google-drive", label: "Google Drive", branding: { iconUrl: "/concepts/product-shot/icons/sources/google-drive.png" } },
];

/** The finished session: the question, the answer, the decisions it links and its sources. */
export const DECISIONS_STORY: ShotStory = {
  workspace: DECISIONS_WORKSPACE,
  question: DECISIONS_QUESTION,
  answerMarkdown: DECISIONS_ANSWER_MARKDOWN,
  times: DECISIONS_TURN_TIMES,
  records: DECISIONS_ANSWER_RECORD_IDS.map((id) => decisionSheetRecord(DECISIONS.find((decision) => decision.id === id)!)),
  trace: { label: runActivityLabel(true), durationLabel: formatRunDuration(DECISIONS_RUN.durationMs), openable: false },
  provenance: {
    sources: buildRunSourceUsage(replayEvents(DECISIONS_RUN, DECISIONS_ANSWER_MARKDOWN)),
    plugins: PLUGINS,
  },
};

/**
 * The landing page's replay: the question sent, the Twin checking the open
 * decisions, Outlook, Xero and Google Drive, the answer streaming in with
 * each decision's evidence and owner, then the first decision's record
 * opening, with the planned date beside the requested one.
 */
export const DECISIONS_REPLAY: ReplayStory = {
  story: DECISIONS_STORY,
  run: DECISIONS_RUN,
  timeline: {
    keyframes: [
      [0, 0],
      [900, 380],
      [1_900, 1_200],
      [2_800, 2_000],
      [3_400, 2_400],
      [4_600, 3_800],
      [5_200, 4_200],
      [6_600, 5_600],
      [7_200, 6_000],
      [7_700, 6_300],
      [8_500, 6_900],
      [9_000, 7_400],
      [14_200, 21_800],
      [14_600, DECISIONS_RUN.durationMs],
    ],
    recordOpensMs: 16_600,
    endsMs: 22_400,
  },
  runningSessions: DECISIONS_SESSIONS_RUNNING,
  openRecordId: DECISIONS_ANSWER_RECORD_IDS[0],
  summary:
    "A replay of a session in a sample workspace: a services firm asks what needs a decision before Friday, the Twin checks its open decisions and the emails, bill and quote behind them in Outlook, Xero and Google Drive, the answer streams in with each decision's evidence and owner, then the first decision's record opens.",
};
